import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { languageSupport, languageService } from '@app/modules/common/language';
import { WSConnection } from '@app/modules/sockets';
import { viewService } from '@app/modules/view';
import { Subscription } from 'rxjs';

interface ManualRow {
    fv: number;         // valor facial (céntimos)
    label: string;      // "2.00", "0.50"…
    available: number;  // piezas dispensables (stacker) de esa moneda
    request: number;    // piezas que el operario elige dar
}

/**
 * "Cambio" (cambiar billete → monedas) modal para GLORY, en DOS fases:
 *
 *   1) `CHANGE_BILL`  → StartCashin: el cliente mete el billete; se muestra el
 *      importe EN VIVO. Al asentarse (idle), el backend hace EndCashin y responde
 *      con `totalDeposited` + `coins` (inventario de monedas dispensables).
 *   2) el operario elige:
 *        - **Automático** → `CHANGE_DISPENSE {amount}`: todo en monedas.
 *        - **Manual** → elige con +/- (tope = disponible) hasta completar el
 *          importe, y `CHANGE_DISPENSE {denominations}`.
 *
 * El billete ya está dentro tras la fase 1, así que SIEMPRE se dispensa: si la
 * modal se cierra sin elegir, se dispensa automático (no atrapar el dinero).
 */
@Component({
    selector: 'ui-modal-glory-change',
    templateUrl: './ui-glory-change.html',
    styleUrls: [ './ui-glory-change.scss' ]
})
export class UiGloryChangeModal extends languageSupport implements OnInit, OnDestroy {
    public _connection: WSConnection = null;
    public _target: string = 'GLORY';

    public status: 'depositing' | 'choose' | 'manual' | 'dispensing' | 'success' | 'error' = 'depositing';
    public currentAmount: number = 0;    // euros introducidos en vivo
    public deposited: number = 0;        // céntimos depositados (fase 1)
    public totalDispensed: number = 0;   // euros devueltos
    public manualRows: ManualRow[] = [];
    public errorMessage: string = null;

    private _opId: string = null;
    private _depositing: boolean = false; // cash-in abierto (fase 1)
    private _dispensed: boolean = false;  // ya se dispensó (o se lanzó)
    private _cancelled: boolean = false;
    private _sub: Subscription = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams, public view: viewService) {
        super(lang, change);
        this._connection = this.params.get('connection');
        this._target = this.params.get('target') || 'GLORY';
    }

    get PhaseLabel(): string {
        switch (this.status) {
            case 'depositing': return this.tr('@smartdrawer_change_insert');
            case 'choose':     return this.tr('@smartdrawer_change_choose');
            case 'manual':     return this.tr('@smartdrawer_change_manual');
            case 'dispensing': return this.tr('@smartdrawer_change_dispensing');
            case 'success':    return this.tr('@smartdrawer_change_done');
            case 'error':      return this.errorMessage || this.tr('@smartdrawer_change_error');
            default:           return '';
        }
    }

    get IsRunning(): boolean {
        return this.status === 'depositing' || this.status === 'dispensing';
    }
    get DepositedEuros(): number { return this.deposited / 100; }
    // Manual: total elegido (céntimos) y si completa el importe exacto.
    get ManualTotal(): number { return this.manualRows.reduce((a, r) => a + r.fv * r.request, 0); }
    get ManualRemaining(): number { return this.deposited - this.ManualTotal; }
    get CanDispenseManual(): boolean { return this.ManualTotal === this.deposited && this.deposited > 0; }

    async ngOnInit() {
        if (!this._connection) {
            this._fail(this.tr('@cashguard_connection_failed'));
            return;
        }
        this._sub = this._connection.OnMessageRecv.subscribe(data => this._onFrame(data));
        this._opId = 'gch' + Date.now();

        try {
            // Fase 1: abrir el cash-in y esperar el billete.
            this._depositing = true;
            const resp = await this._send('CHANGE_BILL', { operationId: this._opId, transactionId: this._opId });
            this._depositing = false;
            if (this._cancelled){ return; }
            if (!this._ok(resp)){
                this._fail(this._body(resp)?.['message'] || null);
                return;
            }
            const body = this._body(resp);
            this.deposited = this._num(body, 'totalDeposited', 0);
            this._buildManualRows(body && body['coins']);
            if (this.deposited <= 0){
                this._fail(this.tr('@smartdrawer_change_nocash'));
                return;
            }
            // Elegir Auto / Manual.
            this.status = 'choose';
        } catch (e) {
            this._depositing = false;
            this._fail();
        }
        this.change.markForCheck();
    }

    ngOnDestroy() {
        super.OnDestroy();
        this._cancelled = true;
        if (this._sub){ this._sub.unsubscribe(); this._sub = null; }
        // Salvaguarda de dinero: si hay depósito sin dispensar, dispensar auto;
        // si el cash-in sigue abierto (nada metido aún), cancelar (devuelve).
        if (this.deposited > 0 && !this._dispensed){
            this._dispensed = true;
            // MISMO operationId que el cash-in abierto: sin él el backend no
            // puede asociar el cash-out y podría rechazarlo, dejando el billete
            // atrapado. `_dispense()` (camino normal) también lo manda.
            this._sendFire('CHANGE_DISPENSE', { operationId: this._opId, transactionId: this._opId, amount: this.deposited });
        } else if (this._depositing){
            this._depositing = false;
            this._sendFire('CANCEL_SALE', { operationId: this._opId, transactionId: this._opId });
        }
    }

    // Automático: todo en monedas.
    async OnAuto() {
        if (this.status !== 'choose'){ return; }
        await this._dispense({ amount: this.deposited });
    }

    // Manual: pasar a la vista de steppers.
    OnManual() {
        if (this.status !== 'choose'){ return; }
        this.status = 'manual';
        this.change.markForCheck();
    }

    OnBackToChoose() {
        if (this.status !== 'manual'){ return; }
        this.status = 'choose';
        this.change.markForCheck();
    }

    incRow(r: ManualRow) {
        // No pasar del disponible ni del importe restante.
        if (r.request < r.available && this.ManualRemaining >= r.fv){ r.request++; this.change.markForCheck(); }
    }
    decRow(r: ManualRow) {
        if (r.request > 0){ r.request--; this.change.markForCheck(); }
    }
    maxRow(r: ManualRow) {
        // Añade el máximo posible sin pasarse del restante ni del disponible.
        const _room = Math.floor(this.ManualRemaining / r.fv);
        r.request = Math.min(r.available, r.request + Math.max(0, _room));
        this.change.markForCheck();
    }

    async OnManualDispense() {
        if (!this.CanDispenseManual){ return; }
        const denominations = this.manualRows
            .filter(r => r.request > 0)
            .map(r => ({ denomination: r.fv, numItems: r.request }));
        await this._dispense({ denominations });
    }

    // Cierra: si hay depósito pendiente, dispensa auto (no atrapar dinero).
    async OnCancel() {
        if (this.status === 'dispensing'){ return; }
        this._cancelled = true;
        if (this.deposited > 0 && !this._dispensed){
            // hay billete dentro → devolver en monedas antes de cerrar.
            await this._dispense({ amount: this.deposited });
            return;
        }
        if (this._depositing){
            this._depositing = false;
            await this._sendSafe('CANCEL_SALE', { operationId: this._opId, transactionId: this._opId });
        }
        this.modalCtrl.dismiss();
    }

    OnClose() {
        this.modalCtrl.dismiss();
    }

    // La X de la cabecera: si hay operación en curso (depósito/elección/manual) se
    // comporta como Cancelar (aborta y devuelve el dinero); en success/error solo
    // cierra. Deshabilitada durante la dispensa (no se puede cancelar un Cashout).
    OnHeaderClose() {
        if (this.status === 'success' || this.status === 'error'){
            this.modalCtrl.dismiss();
            return;
        }
        this.OnCancel();
    }

    /*****************************/
    /* INTERNALS                 */
    /*****************************/

    private async _dispense(extra: any) {
        this.status = 'dispensing';
        this._dispensed = true;
        this.change.markForCheck();
        try {
            const resp = await this._send('CHANGE_DISPENSE', Object.assign({ operationId: this._opId }, extra));
            if (!this._ok(resp)){
                this._fail(this._body(resp)?.['message'] || null);
                return;
            }
            const dispensed = this._num(this._body(resp), 'totalDispensed', -1);
            this.totalDispensed = (dispensed >= 0 ? dispensed : this.deposited) / 100;
            this.status = 'success';
        } catch (e) {
            this._fail();
        }
        this.change.markForCheck();
    }

    private _buildManualRows(coins: any) {
        this.manualRows = [];
        if (!Array.isArray(coins)){ return; }
        this.manualRows = coins
            .map((c: any) => {
                const fv = this._num(c, 'denomination', 0);
                return {
                    fv,
                    label: (fv / 100).toFixed(2),
                    available: this._num(c, 'stacker', 0),
                    request: 0
                };
            })
            // Solo lo que sirve de cambio: una denominación igual o mayor que lo
            // introducido no cambia nada (con 10€, ni el de 10 ni el de 20).
            .filter((r: ManualRow) => r.fv > 0 && r.available > 0 && r.fv < this.deposited);
    }

    private _send(command: string, extra?: any): Promise<any> {
        return this._connection.Send(this._target, Object.assign({ command }, extra || {}));
    }
    private _sendFire(command: string, extra?: any) {
        try { this._send(command, extra).catch(() => {}); } catch (_) {}
    }
    private async _sendSafe(command: string, extra?: any) {
        try { await this._send(command, extra); } catch (_) {}
    }

    private _ok(resp: any): boolean {
        const body = this._body(resp);
        return !!(resp && resp.exec === 'SUCCESS' && (!body || body['status'] !== 'error'));
    }

    private _body(resp: any): any {
        let data = resp && resp['data'];
        if (typeof data === 'string'){
            try { data = JSON.parse(data); } catch (_) { return null; }
        }
        return (data && typeof data === 'object') ? data : null;
    }

    private _num(obj: any, key: string, def: number): number {
        const v = obj && obj[key];
        return (typeof v === 'number') ? v : (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : def);
    }

    private _fail(message?: string) {
        this.status = 'error';
        this.errorMessage = message || this.tr('@smartdrawer_change_error');
        this.change.markForCheck();
    }

    /** Progreso en vivo del depósito (frames del backend durante el Cashin). */
    private _onFrame(msg: any) {
        const body = this._body(msg);
        if (!body || !body['status']){ return; }
        if (body['status'] === 'progress' && body['currentAmount'] !== undefined && this._depositing){
            this.currentAmount = (body['currentAmount'] || 0) / 100;
            this.change.markForCheck();
        }
    }
}
